/**
 * Prescription Repository Interface
 * 처방전 관리 데이터 레이어의 추상화 인터페이스
 */
export class IPrescriptionRepository {
  /**
   * 처방전 업로드
   * @param {Object} data - 처방전 데이터
   * @param {number} data.user_id - 사용자 ID
   * @param {string} data.image_url - 처방전 이미지 URL
   * @returns {Promise<BaseResponse<any>>}
   */
  async uploadPrescription(data) {
    throw new Error('Method must be implemented');
  }

  /**
   * 처방전 목록 조회
   * @param {Object} params - 쿼리 파라미터
   * @param {number} [params.user_id] - 사용자 ID (optional)
   * @param {number} [params.skip=0] - 페이징 시작 위치
   * @param {number} [params.limit=20] - 페이지당 개수
   * @returns {Promise<BaseResponse<any>>}
   */
  async getPrescriptions(params) {
    throw new Error('Method must be implemented');
  }

  /**
   * 처방전 상세 조회
   * @param {number} prescriptionId - 처방전 ID
   * @returns {Promise<BaseResponse<any>>}
   */
  async getPrescriptionById(prescriptionId) {
    throw new Error('Method must be implemented');
  }

  /**
   * 약국 선택 및 주문 생성
   * @param {number} prescriptionId - 처방전 ID
   * @param {Object} data - 약국 선택 데이터
   * @param {number} data.user_id - 사용자 ID
   * @param {number} data.pharmacy_id - 약국 ID
   * @returns {Promise<BaseResponse<any>>}
   */
  async selectPharmacy(prescriptionId, data) {
    throw new Error('Method must be implemented');
  }

  /**
   * 처방전 삭제
   * @param {number} prescriptionId - 처방전 ID
   * @returns {Promise<BaseResponse<any>>}
   */
  async deletePrescription(prescriptionId) {
    throw new Error('Method must be implemented');
  }

  /**
   * 처방전 이미지 분석 (OCR)
   * @param {Object} data - 분석 요청 데이터
   * @param {string} data.prescription_image_url - 처방전 이미지 URL
   * @returns {Promise<BaseResponse<any>>}
   */
  async analyzePrescription(data) {
    throw new Error('Method must be implemented');
  }

  // ==================== 대시보드용 API (약국 인증 필요) ====================

  /**
   * [대시보드] 복약 요청 목록 조회
   * @param {Object} params - 쿼리 파라미터
   * @param {string} [params.status] - 상태 필터 (쉼표 구분 가능: requested,approved)
   * @param {number} [params.page=1] - 페이지 번호
   * @param {number} [params.size=20] - 페이지 크기 (최대 100)
   * @param {string} [params.start_date] - 시작일 (YYYY-MM-DD)
   * @param {string} [params.end_date] - 종료일 (YYYY-MM-DD)
   * @param {string} token - 약국 액세스 토큰
   * @returns {Promise<BaseResponse<any>>}
   */
  async getOrderList(params, token) {
    throw new Error('Method must be implemented');
  }

  /**
   * [대시보드] 복약 요청 상태 변경
   * @param {number} orderId - 주문 ID
   * @param {Object} data - 상태 변경 데이터
   * @param {string} data.status - 변경할 상태 (waiting, approved, rejected, preparing, completed, picked_up)
   * @param {number} [data.total_amount] - 결제 금액 (approved 시 필수)
   * @param {string} [data.rejection_reason] - 거절 사유 (rejected 시 필수)
   * @param {string} [data.preparation_message] - 조제 메시지
   * @param {string} token - 약국 액세스 토큰
   * @returns {Promise<BaseResponse<any>>}
   */
  async updateOrderStatus(orderId, data, token) {
    throw new Error('Method must be implemented');
  }

  /**
   * [대시보드] 복약 요청 상세 조회
   * @param {number} orderId - 주문 ID
   * @param {string} token - 약국 액세스 토큰
   * @returns {Promise<BaseResponse<any>>}
   */
  async getOrderDetail(orderId, token) {
    throw new Error('Method must be implemented');
  }

  /**
   * [대시보드] 환자 목록 조회
   * @param {Object} params - 쿼리 파라미터
   * @param {number} [params.page=1] - 페이지 번호
   * @param {number} [params.size=20] - 페이지 크기
   * @param {string} [params.search] - 검색어
   * @param {string} token - 약국 액세스 토큰
   * @returns {Promise<BaseResponse<any>>}
   */
  async getPatients(params, token) {
    throw new Error('Method must be implemented');
  }

  /**
   * [대시보드] 환자 상세 조회
   * @param {number} userId - 사용자 ID
   * @param {string} token - 약국 액세스 토큰
   * @returns {Promise<BaseResponse<any>>}
   */
  async getPatientDetail(userId, token) {
    throw new Error('Method must be implemented');
  }

  /**
   * [대시보드] 처방전 QR 코드 생성
   * @param {Object} data - QR 생성 요청 데이터
   * @param {string} data.prescription_image_url - 처방전 이미지 URL
   * @param {string} data.qr_type - QR 타입 (paper)
   * @param {string} token - 약국 액세스 토큰
   * @returns {Promise<BaseResponse<any>>}
   */
  async generateQr(data, token) {
    throw new Error('Method must be implemented');
  }

  /**
   * [대시보드] AI 인사이트 텍스트 조회
   * 처방전 단위로 약사가 참고할 1문단 인사이트 (장기 처방·다제 복용 등)
   * @param {number} orderId
   * @returns {Promise<BaseResponse<{insight_text: string}>>}
   */
  async getAiInsight(orderId) {
    throw new Error('Method must be implemented');
  }

  /**
   * [대시보드] 대체조제 후보 조회
   * @param {number} orderId
   * @returns {Promise<BaseResponse<{
   *   original_drug: string,
   *   substitute_drug: string,
   *   price_diff: number,
   *   patient_consent_status: 'pending'|'agreed'|'declined'
   * }>>}
   */
  async getSubstitutions(orderId) {
    throw new Error('Method must be implemented');
  }
}
